/**
 * Entries.tsx
 * Read-only view of Flights / Fuel / Expenses log entries
 * for the current or previous quarter.
 *
 * URL: /entries?tab=flights|fuel|expenses&quarter=current|previous
 *
 * Quarter and tab selection are kept in the URL so the browser back button
 * works and links can be shared.
 */

import { useEffect, useState, type ReactNode } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../auth'
import { getEntries, getConfig, ApiError } from '../api'
import EditEntryModal from '../components/EditEntryModal'
import type { EntryTab, QuarterSel, EntryScope, EntriesResponse, EntryRow } from '../types'

// ---- Helpers ----------------------------------------------------------------

const VALID_TABS: EntryTab[] = ['flights', 'fuel', 'expenses']
const VALID_QUARTERS: QuarterSel[] = ['current', 'previous']
const VALID_SCOPES: EntryScope[] = ['mine', 'all']

function isValidTab(v: string): v is EntryTab {
  return VALID_TABS.includes(v as EntryTab)
}
function isValidQuarter(v: string): v is QuarterSel {
  return VALID_QUARTERS.includes(v as QuarterSel)
}
function isValidScope(v: string): v is EntryScope {
  return VALID_SCOPES.includes(v as EntryScope)
}

/** Pretty tab label */
function tabLabel(tab: EntryTab): string {
  return tab === 'flights' ? 'Flights' : tab === 'fuel' ? 'Fuel' : 'Expenses'
}

/** Format a YYYY-MM-DD date as "Jan 1" for the quarter label subtitle */
function fmtMonthDay(isoDate: string): string {
  const [, m, d] = isoDate.split('-')
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  return `${monthNames[parseInt(m, 10) - 1]} ${parseInt(d, 10)}`
}

/**
 * Which columns to treat as "full-width" (span both grid columns) on a card.
 * Everything else appears in the two-column grid.
 */
const FULL_WIDTH_COLS = new Set(['Notes', 'Description', 'ReceiptURL'])

/**
 * Columns to suppress entirely on the card — Date is shown as the card header,
 * so don't repeat it in the field grid. WaveAccountID is internal bookkeeping
 * (used by the lambda to map expenses to Wave GL accounts) and should never
 * appear in the partner-facing view. Source (Fuel tab) is a deprecated column
 * (source tracking removed 2026-06-10); any legacy values still in the sheet
 * stay there but are hidden from the partner-facing view.
 */
const SUPPRESSED_COLS = new Set(['Date', 'WaveAccountID', 'Source'])

/** Raw sheet header → human-friendly label shown on the card. */
const COL_LABELS: Record<string, string> = {
  PartnerID:        'Partner',
  PaidByPartnerID:  'Paid by',
  PaidBy:           'Paid by',
  TachStart:        'Tach start',
  TachEnd:          'Tach end',
  TotalCost:        'Total cost',
  PricePerGal:      'Price / gal',
  ReceiptURL:       'Receipt',
}

/** Columns whose value is a Partner ID and should render as the partner name. */
const PARTNER_ID_COLS = new Set(['PartnerID', 'PaidByPartnerID', 'PaidBy'])

// ---- Component --------------------------------------------------------------

export default function Entries() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  // Read tab + quarter + scope from URL; fall back to sensible defaults.
  // Default scope is 'mine' so each partner sees only their own rows.
  const rawTab     = searchParams.get('tab')     ?? 'flights'
  const rawQuarter = searchParams.get('quarter') ?? 'current'
  const rawScope   = searchParams.get('scope')   ?? 'mine'
  const tab: EntryTab        = isValidTab(rawTab)     ? rawTab     : 'flights'
  const quarter: QuarterSel  = isValidQuarter(rawQuarter) ? rawQuarter : 'current'
  const scope: EntryScope    = isValidScope(rawScope) ? rawScope   : 'mine'

  const [data, setData]       = useState<EntriesResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError]     = useState<string | null>(null)

  // Caller's partner ID — needed to tell which rows are editable when
  // scope === 'all'. We fetch it once via /api/config (already cached by the
  // browser's fetch layer for the rest of the session) and keep it in state.
  const [callerPartnerId, setCallerPartnerId] = useState<string>('')

  // PartnerID → Partner Name lookup, used to render P1/P2/... as friendly
  // names across all three views. Empty until the config fetch resolves; the
  // value-render helper falls back to the raw ID until then.
  const [partnerNameById, setPartnerNameById] = useState<Record<string, string>>({})

  // Fuel price-per-gallon ceiling from the Config tab. Undefined if not set;
  // when undefined the PricePerGal cell renders without over-cap highlighting.
  const [maxFuelPricePerGal, setMaxFuelPricePerGal] = useState<number | undefined>(undefined)

  // Row currently being edited; null means no modal open. Owner+quarter
  // gates are enforced server-side too — this is just a UI filter so the
  // modal never opens on a row that can't be saved.
  const [editingRow, setEditingRow] = useState<EntryRow | null>(null)

  // refresh token — bumping this re-runs the fetch effect after a save/delete.
  const [refreshTick, setRefreshTick] = useState(0)

  useEffect(() => {
    let cancelled = false
    getConfig()
      .then((cfg) => {
        if (cancelled) return
        setCallerPartnerId(cfg.caller?.id ?? '')
        const map: Record<string, string> = {}
        for (const p of cfg.partners ?? []) map[p.id] = p.name
        setPartnerNameById(map)
        setMaxFuelPricePerGal(cfg.maxFuelPricePerGal)
      })
      .catch(() => { /* non-fatal; without caller id, scope=mine still works */ })
    return () => { cancelled = true }
  }, [])

  // Fetch whenever tab, quarter, or scope changes (or after a save/delete).
  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)

    getEntries(tab, quarter, scope)
      .then((res) => {
        if (!cancelled) {
          setData(res)
          setLoading(false)
        }
      })
      .catch((err) => {
        if (cancelled) return
        if (err instanceof ApiError && err.status === 403) {
          navigate('/not-authorized')
          return
        }
        setError(err instanceof Error ? err.message : 'Could not load entries.')
        setLoading(false)
      })

    return () => { cancelled = true }
  }, [tab, quarter, scope, navigate, refreshTick])

  /**
   * A row is editable iff it's in the current quarter AND the caller owns it.
   * Owner column varies by tab; see entries.ts comments and entryGate.ts.
   * For scope='mine' every row is the caller's by construction; for scope='all'
   * we filter against the caller's partner ID resolved from /api/config.
   */
  function isEditable(row: EntryRow): boolean {
    if (quarter !== 'current') return false
    if (!row._rowIndex) return false
    if (!callerPartnerId) return scope === 'mine'
    const ownerKey =
      tab === 'flights' ? 'PartnerID'
      : tab === 'fuel'   ? 'PaidByPartnerID'
      :                    'PaidBy'
    return (row[ownerKey] ?? '').trim() === callerPartnerId
  }

  // Update URL param without pushing a new history entry for the same key
  function setTab(t: EntryTab) {
    setSearchParams({ tab: t, quarter, scope }, { replace: false })
  }
  function setQuarter(q: QuarterSel) {
    setSearchParams({ tab, quarter: q, scope }, { replace: false })
  }
  function setScope(s: EntryScope) {
    setSearchParams({ tab, quarter, scope: s }, { replace: false })
  }

  // Quarter subtitle, e.g. "Jul 1 – Sep 30"
  const quarterSubtitle = data
    ? `${fmtMonthDay(data.quarter.startDate)} – ${fmtMonthDay(data.quarter.endDate)}`
    : ''

  return (
    <div className="wrap">
      {/* ---- Brand header ---- */}
      <div className="brand">
        <div className="brand-logo">N73AA</div>
        <div className="brand-tag">
          <Link to="/" className="back-link">&#8592; Back</Link>
          &ensp;View Entries
        </div>
      </div>

      {user && (
        <div className="who">
          Signed in as: <strong>{user.name}</strong>
        </div>
      )}

      {/* ---- Quarter selector ---- */}
      <div className="pill-group">
        <button
          className={quarter === 'current' ? 'pill-active' : ''}
          onClick={() => setQuarter('current')}
        >
          Current Quarter
        </button>
        <button
          className={quarter === 'previous' ? 'pill-active' : ''}
          onClick={() => setQuarter('previous')}
        >
          Previous Quarter
        </button>
      </div>

      {/* ---- Tab selector ---- */}
      <div className="pill-group">
        {VALID_TABS.map((t) => (
          <button
            key={t}
            className={tab === t ? 'pill-active' : ''}
            onClick={() => setTab(t)}
          >
            {tabLabel(t)}
          </button>
        ))}
      </div>

      {/* ---- Scope selector ---- */}
      <div className="pill-group">
        <button
          className={scope === 'mine' ? 'pill-active' : ''}
          onClick={() => setScope('mine')}
        >
          My Entries
        </button>
        <button
          className={scope === 'all' ? 'pill-active' : ''}
          onClick={() => setScope('all')}
        >
          All Partners
        </button>
      </div>

      {/* ---- Quarter label ---- */}
      {data && (
        <div className="quarter-label">
          {data.quarter.label}
          {quarterSubtitle ? ` (${quarterSubtitle})` : ''}
        </div>
      )}

      {/* ---- Content ---- */}
      {loading && (
        <div className="entries-loading">Loading&hellip;</div>
      )}

      {!loading && error && (
        <div className="msg msg-err">{error}</div>
      )}

      {!loading && !error && data && data.rows.length === 0 && (
        <div className="entries-empty">
          No {tabLabel(tab).toLowerCase()} entries for {data.quarter.label}.
        </div>
      )}

      {!loading && !error && data && data.rows.length > 0 && (
        <>
          {[...data.rows]
            .sort((a, b) => (b['Date'] ?? '').localeCompare(a['Date'] ?? ''))
            .map((row, i) => {
            // Non-date, non-suppressed columns for the field grid
            const gridCols = data.columns.filter((c) => !SUPPRESSED_COLS.has(c))
            const editable = isEditable(row)

            return (
              <div
                key={i}
                className={'entry-card' + (editable ? ' entry-card--editable' : '')}
                role={editable ? 'button' : undefined}
                tabIndex={editable ? 0 : undefined}
                onClick={editable ? () => setEditingRow(row) : undefined}
                onKeyDown={editable
                  ? (e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        setEditingRow(row)
                      }
                    }
                  : undefined}
                title={editable ? 'Tap to edit or delete' : undefined}
              >
                <div className="entry-card-date">{row['Date'] ?? '—'}</div>
                <div className="entry-card-fields">
                  {gridCols.map((col) => {
                    const rawVal = (row[col] ?? '').trim()
                    // Skip empty ReceiptURL (no link to show)
                    if (!rawVal && col === 'ReceiptURL') return null
                    // Skip empty Notes / Description to save space
                    if (!rawVal && (col === 'Notes' || col === 'Description')) return null

                    // PricePerGal gets special handling — compute from
                    // TotalCost/Gallons when the sheet cell is blank, and
                    // highlight when it exceeds the configured cap.
                    if (col === 'PricePerGal') {
                      const node = renderPricePerGal(row, rawVal, maxFuelPricePerGal)
                      if (!node) return null
                      return (
                        <div key={col} className="entry-card-field">
                          <div className="entry-card-field-label">
                            {COL_LABELS[col] ?? col}
                          </div>
                          <div className="entry-card-field-value">{node}</div>
                        </div>
                      )
                    }

                    // Default value rendering: partner IDs become names,
                    // receipts become a link, everything else is plain text.
                    let valueNode: ReactNode
                    if (col === 'ReceiptURL' && rawVal) {
                      valueNode = (
                        <a href={rawVal} target="_blank" rel="noopener noreferrer">
                          View receipt
                        </a>
                      )
                    } else if (PARTNER_ID_COLS.has(col) && rawVal) {
                      valueNode = partnerNameById[rawVal] ?? rawVal
                    } else {
                      valueNode = rawVal || '—'
                    }

                    return (
                      <div
                        key={col}
                        className={
                          'entry-card-field' +
                          (FULL_WIDTH_COLS.has(col) ? ' entry-card-field--full' : '')
                        }
                      >
                        <div className="entry-card-field-label">
                          {COL_LABELS[col] ?? col}
                        </div>
                        <div className="entry-card-field-value">{valueNode}</div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )
            })}
        </>
      )}

      {/* ---- Google Sheets link ---- */}
      {data && (
        <a
          className="entries-sheet-link"
          href={data.spreadsheetUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          Open in Google Sheets &#8599;
        </a>
      )}

      <div className="page-foot">N73AA partnership log.</div>

      {editingRow && (
        <EditEntryModal
          tab={tab}
          row={editingRow}
          callerPartnerId={callerPartnerId}
          onClose={() => setEditingRow(null)}
          onSaved={() => {
            setEditingRow(null)
            setRefreshTick((n) => n + 1)
          }}
        />
      )}
    </div>
  )
}

/**
 * Render the PricePerGal cell for a fuel row. Falls back to TotalCost/Gallons
 * when the sheet's PricePerGal cell is blank — the submit form computes it but
 * legacy rows from before that change may be empty. Highlights values that
 * exceed the partnership cap with an "Over cap by $X.XX" subtitle.
 *
 * Returns null when there's nothing usable to show, so the caller can hide
 * the whole field entry.
 */
function renderPricePerGal(
  row: EntryRow,
  rawVal: string,
  cap: number | undefined,
): ReactNode | null {
  let price = parseFloat(rawVal)
  if (isNaN(price) || price <= 0) {
    // Compute from total / gallons when the stored value is empty.
    const total = parseFloat(row['TotalCost'] ?? '')
    const gal   = parseFloat(row['Gallons']   ?? '')
    if (isNaN(total) || isNaN(gal) || gal <= 0) return null
    price = total / gal
  }

  const display = `$${price.toFixed(2)}`
  const overCap = cap !== undefined && price > cap
  if (!overCap) return display

  const excess = price - (cap as number)
  return (
    <>
      <span className="entry-price-over">{display}</span>
      <div className="entry-price-overcap-note">
        Over cap by ${excess.toFixed(2)}
      </div>
    </>
  )
}
